/**
 * Compact "Medication Details" section rendered further down each product
 * landing page. Reads `treatment.medDetails` — either a single object with
 * { concentration | formulation, administration } or an object with
 * `options: [{ label, ...same fields }]` for products that consolidate
 * multiple formats (e.g. NAD+ Injection + Nasal Spray).
 *
 * Purely informational — no clinical dosing, no total vial content, no
 * repeated universal disclosures (prescription / compounded) which already
 * appear elsewhere on the site.
 */

function DetailRow({ label, children }) {
  return (
    <div className="grid grid-cols-[8rem_1fr] sm:grid-cols-[10rem_1fr] items-baseline gap-3 py-2.5">
      <dt className="text-xs sm:text-sm text-[#5E5C58] uppercase tracking-[0.08em]">
        {label}
      </dt>
      <dd className="text-sm sm:text-[15px] text-[#2C2B29] leading-snug">
        {children}
      </dd>
    </div>
  );
}

function DetailGroup({ entry, slug, optionKey = "" }) {
  const idPrefix = optionKey ? `med-details-${slug}-${optionKey}` : `med-details-${slug}`;
  const isFormulation = Array.isArray(entry.formulation);
  return (
    <dl className="divide-y divide-[#E8E5DF]" data-testid={idPrefix}>
      {isFormulation ? (
        <DetailRow label="Formulation">
          <ul className="space-y-1">
            {entry.formulation.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </DetailRow>
      ) : (
        <DetailRow label="Concentration">{entry.concentration}</DetailRow>
      )}
      {entry.size && <DetailRow label="Size">{entry.size}</DetailRow>}
      <DetailRow label="Administration">{entry.administration}</DetailRow>
    </dl>
  );
}

export default function MedicationDetails({ treatment }) {
  const md = treatment?.medDetails;
  if (!md) return null;
  const options = Array.isArray(md.options) ? md.options : null;

  return (
    <section
      data-testid={`medication-details-${treatment.slug}`}
      aria-labelledby={`med-details-heading-${treatment.slug}`}
    >
      <details
        data-testid={`medication-details-accordion-${treatment.slug}`}
        className="group bg-white/70 backdrop-blur-sm border border-[#E8E5DF] rounded-2xl overflow-hidden"
      >
        <summary
          data-testid={`medication-details-toggle-${treatment.slug}`}
          className="flex items-center justify-between gap-3 px-5 py-4 md:py-5 cursor-pointer list-none select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C96A4E]/30 hover:bg-[#FAF9F6]/60 transition-colors"
        >
          <h2
            id={`med-details-heading-${treatment.slug}`}
            className="font-display text-xl md:text-2xl font-semibold text-[#2C2B29]"
          >
            Medication Details
          </h2>
          <span
            aria-hidden="true"
            className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-[#E8E5DF] text-[#5E5C58] text-[13px] leading-none transition-transform duration-200 group-open:rotate-180 shrink-0"
          >
            ▾
          </span>
        </summary>
        <div className="px-5 pb-4">
          {options ? (
            <div className="divide-y divide-[#E8E5DF]">
              {options.map((opt) => {
                const key = opt.label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                return (
                  <div key={opt.label} className="py-3 first:pt-1 last:pb-1">
                    <p className="text-[13px] font-semibold text-[#2C2B29] mb-1">
                      {opt.label}
                    </p>
                    <DetailGroup entry={opt} slug={treatment.slug} optionKey={key} />
                  </div>
                );
              })}
            </div>
          ) : (
            <DetailGroup entry={md} slug={treatment.slug} />
          )}
        </div>
      </details>
    </section>
  );
}
